/**
 * Admin Center → Konfiguration → Zuständigkeiten.
 *
 * Einmal festgelegt: welcher Schritt bei welcher Rolle liegt. Zwei Spalten,
 * Kalkulant und Versender – eine dritte Rolle gibt es nicht (Richtlinie 9).
 * Jeder Klick speichert sofort; Änderungen gelten ab dem nächsten
 * Schrittwechsel, weil die Tische bei jeder Anzeige neu gerechnet werden.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import type { ZustRolle, ZustaendigkeitStand, ZustaendigkeitZeile } from '../typen';
import './zustaendigkeiten.css';

export function Zustaendigkeiten() {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();

  const abfrage = useQuery({
    queryKey: ['zustaendigkeit'],
    queryFn: () => api.hole<ZustaendigkeitStand>('/zustaendigkeit'),
  });

  const setzen = useMutation({
    mutationFn: ({ schritt, rolle }: { schritt: string; rolle: ZustRolle }) =>
      api.ersetze<{ zeilen: ZustaendigkeitZeile[] }>('/zustaendigkeit', { zeilen: [{ schritt, rolle }] }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['zustaendigkeit'] });
      /* Die Tische ändern sich mit – alles, was Vorgänge zeigt, neu holen. */
      void queryClient.invalidateQueries({ queryKey: ['mein-tag'] });
      void queryClient.invalidateQueries({ queryKey: ['angebote'] });
      void queryClient.invalidateQueries({ queryKey: ['abwicklung'] });
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung nicht möglich.'),
  });

  const d = abfrage.data;
  if (!d) return <div className="modal-hinweis">Zuständigkeiten werden geladen …</div>;

  const angebot = d.zeilen.filter((z) => z.gruppe === 'angebot');
  const auftrag = d.zeilen.filter((z) => z.gruppe === 'auftrag');
  const kachelLabel = (key: string) => d.kacheln.find((k) => k.key === key)?.label ?? key;

  function Tabelle({ titel, zeilen }: { titel: string; zeilen: ZustaendigkeitZeile[] }) {
    return (
      <div className="zu-tabelle">
        <div className="zu-zeile zu-kopf">
          <span>{titel}</span>
          {d!.rollen.map((r) => (
            <span key={r.key} className="zu-spalte">
              {r.label}
            </span>
          ))}
        </div>
        {zeilen.map((z) => (
          <div className="zu-zeile" key={z.schritt}>
            <span className="zu-schritt">
              <span className="zu-schritt-name">{z.label}</span>
              <span className="zu-schritt-unter">
                Kachel {kachelLabel(z.kachel)}
                {z.kachel === 'wiedervorlage' ? ' · ruht' : ''}
                {z.rolle !== z.vorgabe && z.geaendertVon ? ` · geändert von ${z.geaendertVon}` : ''}
              </span>
            </span>
            {d!.rollen.map((r) => (
              <span key={r.key} className="zu-spalte">
                <button
                  type="button"
                  role="radio"
                  aria-checked={z.rolle === r.key}
                  aria-label={`${z.label}: ${r.label}`}
                  title={`${z.label} liegt beim ${r.label}${z.vorgabe === r.key ? ' (Vorbelegung)' : ''}`}
                  className={`zu-punkt ${z.rolle === r.key ? 'an' : ''}`}
                  disabled={setzen.isPending}
                  onClick={() => z.rolle !== r.key && setzen.mutate({ schritt: z.schritt, rolle: r.key })}
                >
                  <span />
                </button>
              </span>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <section className="ein-abschnitt zu">
      <div className="zu-kopfzeile">
        <div>
          <div className="ein-titel">Zuständigkeiten</div>
          <div className="zu-untertitel">Einmal festgelegt: welcher Schritt bei welcher Rolle liegt</div>
        </div>
        <div className="ein-hinweis zu-erklaerung">
          Am Angebot stehen zwei Namen – Kalkulant und Versender. Diese Tabelle entscheidet, auf
          wessen Tisch der Vorgang in jedem Schritt liegt. Änderungen gelten ab dem nächsten
          Schrittwechsel.
        </div>
      </div>

      <div className="zu-tabellen">
        <Tabelle titel="Angebot" zeilen={angebot} />
        <Tabelle titel="Auftrag · je Bestellung" zeilen={auftrag} />
      </div>

      <div className="ein-hinweis">
        Vorbelegt ist: Kalkulant für alles, was Rechnen und Prüfen ist – Kalkulieren,
        Angebotsprüfung, Aufmaß, Bestellung, AB prüfen, AB retour. Versender für alles, was Kunde
        und Termin ist – Ankündigung, Versand, Nachfassen, Liefertermin, Montage, Abnahme, Service.
        Wer gerechnet hat, prüft; wer den Kunden kennt, telefoniert. Einen einzelnen Vorgang gibt
        man im Vorgang selbst weiter – mit Begründung, nur für den aktuellen Schritt.
      </div>
    </section>
  );
}
